import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, Brain, Target, Zap, ChevronDown, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const LandingMonica = () => {
  const navigate = useNavigate();
  const [typedText, setTypedText] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  const fullText = "Antes de vender, debes entender.";

  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 80);
    return () => clearInterval(timer);
  }, []);

  const handleAnalyze = () => {
    if (!urlInput) return;
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
      setShowInsights(true);
    }, 2500);
  };

  const insights = [
    {
      icon: <Brain className="w-5 h-5" />,
      text: "Tu cliente habla de resultados, no de beneficios.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Target className="w-5 h-5" />,
      text: "Tu marca suena técnica, tu cliente quiere claridad.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      text: "Tu oferta promete mucho, pero no explica el cómo.",
      color: "from-orange-500 to-red-500"
    }
  ];

  const icpFeatures = [
    { label: "ICP Primario", description: "Perfil demográfico y psicográfico completo" },
    { label: "ICP Secundario", description: "Audiencia alternativa identificada" },
    { label: "Triggers Emocionales", description: "Emociones que activan la compra" },
    { label: "Puntos de Dolor", description: "Problemas específicos de tu cliente" },
    { label: "Deseos Profundos", description: "Lo que realmente quieren lograr" },
    { label: "Objeciones Reales", description: "Razones por las que no compran" },
    { label: "Drivers Psicológicos", description: "Motivaciones que guían decisiones" },
    { label: "Lenguaje de Compra", description: "Palabras exactas que usan" },
    { label: "Mensajes Listos", description: "Copy optimizado para convertir" },
    { label: "Estrategia Completa", description: "Plan de marketing end-to-end" },
    { label: "Guiones para Ads", description: "Scripts listos para video" },
    { label: "CTAs Optimizados", description: "Llamados a la acción que convierten" }
  ];

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-screen flex flex-col items-center justify-center px-6 py-20"
        style={{ opacity }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-black"></div>
          <motion.div
            className="absolute inset-0"
            animate={{
              background: [
                'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)',
                'radial-gradient(circle at 80% 50%, rgba(147, 51, 234, 0.15) 0%, transparent 50%)',
                'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)',
              ],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          />
        </div>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 mb-12"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_strategyai-7/artifacts/mzdwsitj_IMG_6812.jpeg" 
            alt="Mónica" 
            className="h-20 w-auto"
          />
        </motion.div>

        {/* Typed Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="relative z-10 text-center mb-8"
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            {typedText}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className="inline-block w-1 h-16 md:h-20 bg-blue-500 ml-2 align-middle"
            />
          </h1>
        </motion.div>

        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 4, duration: 1 }}
          className="relative z-10 text-center max-w-5xl mx-auto"
        >
          <h2 className="text-6xl md:text-8xl font-bold mb-8 leading-tight">
            No necesitas
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              más anuncios.
            </span>
          </h2>
          <h3 className="text-4xl md:text-6xl font-light mb-12 text-gray-400">
            Necesitas entender a tu cliente.
          </h3>
          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto">
            Tu página web oculta más información de la que crees.<br />
            Mónica la analiza y te entrega claridad estratégica lista para convertir.
          </p>

          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              onClick={() => navigate('/app/auth')}
              className="bg-white text-black hover:bg-gray-200 text-lg px-12 py-8 rounded-full font-semibold transition-all duration-300 shadow-2xl shadow-blue-500/50"
            >
              Probar Mónica Gratis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>

          <p className="text-sm text-gray-500 mt-6">
            10 personas por día. No vendemos volumen, vendemos precisión.
          </p>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-gray-600" />
        </motion.div>
      </motion.section>

      {/* The WHY Section */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
              Las empresas no fracasan
              <br />
              por falta de marketing.
            </h2>
            <h3 className="text-4xl md:text-5xl font-light text-gray-400">
              Fracasan por falta de claridad.
            </h3>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            viewport={{ once: true }}
            className="text-2xl text-center text-gray-300 mb-20 max-w-4xl mx-auto"
          >
            El 99% de negocios habla sin entender a quién le hablan.
            <br />
            <span className="text-white font-semibold">Mónica existe para resolver eso.</span>
          </motion.p>

          {/* Micro-phrases */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              "Sin claridad, no hay estrategia.",
              "Sin estrategia, no hay resultados.",
              "Sin resultados, no hay negocio."
            ].map((phrase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="relative p-8 bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-gray-800 hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="absolute top-4 left-4 text-6xl font-bold text-gray-800">
                  {index + 1}
                </div>
                <p className="text-xl font-light relative z-10 pt-12">
                  {phrase}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The HOW Section */}
      <section className="relative py-32 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-7xl font-bold mb-8">
              El mecanismo es simple.
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                El resultado es poderoso.
              </span>
            </h2>
          </motion.div>

          {/* Steps */}
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Tu Página", desc: "Le das a Mónica tu URL" },
              { num: "02", title: "Análisis IA", desc: "Mónica analiza tu marca, lenguaje y oferta" },
              { num: "03", title: "Cliente Ideal", desc: "Te devuelve tu ICP completo" },
              { num: "04", title: "Estrategia Lista", desc: "Construye guiones, ads y estrategia" }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15, duration: 0.8 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="relative p-8 bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 group-hover:border-blue-500/50 transition-all duration-300 h-full">
                  <div className="text-7xl font-bold text-gray-800 mb-4">
                    {step.num}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-400">{step.desc}</p>
                  
                  {/* Connection Line */}
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-blue-500 to-transparent"></div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The WHAT Section */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-7xl font-bold mb-8">
              Esto es lo que obtienes.
            </h2>
            <p className="text-2xl text-gray-400 max-w-3xl mx-auto">
              No una herramienta más. Un sistema completo de inteligencia de cliente.
            </p>
          </motion.div>

          {/* ICP Features Grid */}
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {icpFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="relative p-6 bg-gradient-to-br from-gray-900 to-black rounded-xl border border-gray-800 hover:border-blue-500/50 transition-all duration-300 cursor-pointer group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/10 group-hover:to-purple-500/10 rounded-xl transition-all duration-300"></div>
                <div className="relative z-10">
                  <Check className="w-5 h-5 text-blue-400 mb-3" />
                  <h3 className="text-lg font-bold mb-2">{feature.label}</h3>
                  <p className="text-sm text-gray-400">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="text-xl text-gray-400 mb-8">
              Todo en minutos. Con precisión quirúrgica. Sin fricción.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="relative py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Sparkles className="w-12 h-12 mx-auto mb-6 text-blue-400" />
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Prueba ahora.
            </h2>
            <p className="text-xl text-gray-400">
              Ingresa tu URL y descubre 3 insights sobre tu cliente.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            viewport={{ once: true }}
            className="relative p-8 md:p-12 bg-gradient-to-br from-gray-900 via-gray-950 to-black rounded-3xl border border-gray-800"
          >
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <Input
                type="url"
                placeholder="https://tuempresa.com"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                className="flex-1 bg-black border-gray-700 text-white text-lg px-6 py-6 rounded-full focus:border-blue-500"
                disabled={isAnalyzing}
              />
              <Button
                onClick={handleAnalyze}
                disabled={isAnalyzing || !urlInput}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 rounded-full text-lg font-semibold transition-all duration-300"
              >
                {isAnalyzing ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkles className="w-5 h-5" />
                  </motion.div>
                ) : (
                  <>
                    Analizar
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>

            {isAnalyzing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="inline-block"
                >
                  <Brain className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                </motion.div>
                <p className="text-gray-400">Analizando tu marca...</p>
              </motion.div>
            )}

            {showInsights && !isAnalyzing && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {insights.map((insight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.2 }}
                    className={`flex items-start gap-4 p-6 bg-gradient-to-r ${insight.color} bg-opacity-10 rounded-2xl border border-gray-800`}
                  >
                    <div className={`p-3 rounded-full bg-gradient-to-r ${insight.color}`}>
                      {insight.icon}
                    </div>
                    <p className="text-lg flex-1 pt-2">{insight.text}</p>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="text-center pt-8"
                >
                  <p className="text-gray-400 mb-6">
                    Esto es solo el inicio. El análisis completo incluye 12+ componentes estratégicos.
                  </p>
                  <Button
                    onClick={() => navigate('/app/auth')}
                    className="bg-white text-black hover:bg-gray-200 px-8 py-6 rounded-full text-lg font-semibold"
                  >
                    Ver Análisis Completo
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Authority Section */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Creado por la empresa que está redefiniendo
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                el futuro de la inteligencia prospectiva en LATAM.
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Detrás de Mónica está <span className="text-white font-semibold">Urpe AI Lab</span> y la visión de{' '}
              <span className="text-white font-semibold">Diego Urquijo</span>, empresario que ha escalado más de 25 compañías, 
              entrenado a miles de emprendedores y construido uno de los ecosistemas de IA más avanzados de la región.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-6xl md:text-8xl font-bold mb-8">
              La claridad es
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                una decisión.
              </span>
            </h2>
            <p className="text-2xl text-gray-400 mb-12">
              Empieza hoy.
            </p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                onClick={() => navigate('/app/auth')}
                className="bg-white text-black hover:bg-gray-200 text-xl px-16 py-8 rounded-full font-bold transition-all duration-300 shadow-2xl shadow-blue-500/50"
              >
                Probar Gratis
                <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
            </motion.div>

            <p className="text-sm text-gray-500 mt-8">
              10 personas por día. No vendemos volumen, vendemos precisión.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-6 border-t border-gray-900 bg-black">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_strategyai-7/artifacts/mzdwsitj_IMG_6812.jpeg" 
              alt="Mónica" 
              className="h-8 w-auto"
            />
            <span className="text-gray-500">Powered by Urpe AI Lab</span>
          </div>
          
          <div className="flex gap-8 text-sm text-gray-500">
            <a href="#" className="hover:text-white transition-colors">Términos</a>
            <a href="#" className="hover:text-white transition-colors">Privacidad</a>
            <a href="#" className="hover:text-white transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingMonica;
